import {
    Grid,
    MenuItem,
    Select,
    SelectChangeEvent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    OutlinedInput,
    DialogActions,
    Checkbox,
    Container,
    Card,
    CardContent,
    CardActions,
    Typography,
    Box,
    Alert,
} from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import {models, Report} from 'powerbi-client';
import {PowerBIEmbed} from 'powerbi-client-react';
import {Warning, Info} from '@mui/icons-material';
import '../../componentCss/reporting.css';
import {useAppDispatch, useAppSelector} from '../../redux/functions/hooks';
import {
    BillToMinimalVm,
    useCreateBookmarkMutation,
    useDeleteBookmarkMutation,
    useGetApiReportingQuery,
    useGetBillToForDisplayQuery,
    useGetBookmarksForUserQuery,
} from '../../redux/rtkquery/paylogikApi';
import {
    setReportBookmarks,
    setUserBookmarks,
} from '../../redux/store/reportingSlice';
import {PLFormLabel} from '../common/ToolTipLabels';
import SSMultiSelect from '../common/PLMultiSelect';
import {action, CanI} from '../../CanI';
import {ControlledLoading, Loading} from '../common/Loading';
import PLSpeedDial, {Action} from '../common/PLSpeedDial';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import {toast} from '../../functions/toast';

const Reporting = ({}) => {
    const [bookmarkModalOpen, setBookMarkModalOpen] = useState<boolean>(false);
    const [bookmarkModalBookmarkName, setBookmarkModalBookmarkName] =
        useState<string>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    
    const {data: getEmbedInfoData, isLoading: getEmbedInfoLoading, error: embedError, isError: isEmbedError, refetch: refetchEmbed} =
        useGetApiReportingQuery();

    const {data: getBilltosData, isLoading: getBillTosLoading, error: billTosError, isError: isBillTosError, refetch: refetchBillTos} =
        useGetBillToForDisplayQuery();

    const [bookmarkModalSelectedBilltos, setBookmarkModalSelectedBilltos] =
        useState<BillToMinimalVm[]>([]);

    const [bookmarkModalPublicCheckBox, setBookmarkModalPublicCheckBox] =
        useState<boolean>(false);

    const [
        createBookmark,
        {data: createBookmarkData, isLoading: createBookmarkIsLoading},
    ] = useCreateBookmarkMutation();

    const [
        deleteBookmark,
        {data: deleteBookmarkData, isLoading: deleteBookmarkIsLoading},
    ] = useDeleteBookmarkMutation();

    const {data: bookmarksForUser, isLoading: bookmarksLoading} =
        useGetBookmarksForUserQuery();

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (bookmarksForUser) {
            dispatch(setUserBookmarks(bookmarksForUser));
        }
    }, [bookmarksForUser]);

    useEffect(() => {
        if (createBookmarkData) {
            dispatch(setUserBookmarks(createBookmarkData));
        }
    }, [createBookmarkData]);

    useEffect(() => {
        if (deleteBookmarkData) {
            dispatch(setUserBookmarks(deleteBookmarkData));
        }
    }, [deleteBookmarkData]);

    const [report, setReport] = useState<Report>();
    //const [bookmarks, setBookmarks] = useState<Bookmark[]>();
    const [selectedBookmark, setSelectedBookmark] = useState<number>(null);

    const reportBookmarks = useAppSelector((x) => x.reporting.reportBookmarks);

    const bookmarks = useAppSelector((x) => [
        ...x.reporting.reportBookmarks,
        ...x.reporting.bookmarks,
    ]);

    //When selecting a bookmark, we need to use the applyState function from the report API.
    const handleChange = (event: SelectChangeEvent) => {
        //@ts-ignore
        setSelectedBookmark(event.target.value);

        const bm = bookmarks.find(
            //@ts-ignore
            (x) => x.id == event.target.value
        )?.bookmarkBase64;

        if (bm) report.bookmarksManager.applyState(bm);
    };

    //to create a book mark, we can capture the state with the report API and then store that state "Somewhere"
    //Right now its stored in redux so disapears whenever you delete the browser cache.  That will be a bad thing to do though.
    //We will want an API call to get saved bookmarks for the user from a database somewhere.
    //We will also want a URL users can share to pass around bookmarks which means we will need some sort of permissions around those as well.
    //We can probably have user level, customer level, and global saved bookmarks.  Possibly a single bookmarks table with a parent type? not sure how that join would work though.
    const openBookmarkModal = () => {
        setBookMarkModalOpen(true);
    };

    const clearBookmarkModal = () => {
        setBookmarkModalPublicCheckBox(false);
        setBookmarkModalSelectedBilltos([]);
        setBookmarkModalBookmarkName(null);
    };

    const cancelBookmarkCreate = () => {
        clearBookmarkModal();
        setBookMarkModalOpen(false);
    };

    const okBookmarkCreate = () => {
        if (!bookmarkModalBookmarkName) {
            //TODO: Add validation
        }

        report.bookmarksManager.capture().then((x) =>
            createBookmark({
                createReportBookmarkVm: {
                    bookmark: {
                        //@ts-ignore
                        reportId: report.config.id,
                        displayName: bookmarkModalBookmarkName,
                        bookmarkBase64: x.state,
                    },
                    isPublic: bookmarkModalPublicCheckBox,
                    billTos: bookmarkModalSelectedBilltos,
                },
            })
                .unwrap()
                .then(() => {
                    clearBookmarkModal();
                    toast.success('Bookmark created successfully');
                })
                .catch((error) => {
                    const message = error?.data?.message || 'Failed to create bookmark. Please try again.';
                    toast.error(message);
                    setErrorMessage(message);
                })
        );
        setBookMarkModalOpen(false);
    };

    const handleRemoveBookmark = () => {
        if (reportBookmarks.some((x) => x.id == selectedBookmark)) {
            //TODO: Use a Toast message.
            toast.error('This bookmark cannot be removed');
        } else {
            deleteBookmark({id: selectedBookmark})
                .unwrap()
                .then(() =>
                    toast.success('Bookmark deleted successfully')
                )
                .catch((error) => {
                    const message = error?.data?.message || 'Failed to delete bookmark. Please try again.';
                    toast.error(message);
                    setErrorMessage(message);
                });
        }

        setSelectedBookmark(null);
    };

    //Since most of this stuff wont change over renders, just use memo to cut down on the churn.
    const reportConfig = useMemo<models.IReportEmbedConfiguration>(() => {
        return {
            type: 'report',
            id: getEmbedInfoData?.embedReport[0].reportId,
            embedUrl: getEmbedInfoData?.embedReport[0].embedUrl,
            accessToken: getEmbedInfoData?.embedToken.token,
            tokenType: models.TokenType.Embed, // Use models.TokenType.Aad for SaaS embed

            settings: {
                panes: {
                    filters: {
                        expanded: false,
                        visible: true,
                    },
                    pageNavigation: {
                        visible: false,
                    },
                    bookmarks: {
                        expanded: false,
                        visible: false,
                    },
                },
                background: models.BackgroundType.Transparent,
            },
        };
    }, [getEmbedInfoData]);

    const actions = [
        {icon: <SaveIcon />, name: 'Save Bookmark', onClick: openBookmarkModal},
        {
            icon: <DeleteForeverIcon />,
            name: 'Delete Bookmark',
            onClick: handleRemoveBookmark,
        },
        {
            icon: (
                <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    //@ts-ignore
                    value={selectedBookmark}
                    label="Age"
                    onChange={handleChange}
                    fullWidth={true}>
                    {bookmarks.map((x) => (
                        <MenuItem value={x.id}>{x.displayName}</MenuItem>
                    ))}
                </Select>
            ),
            name: 'Select Bookmark',
            fabProps: {variant: 'extended'},
        },
        //{icon: <PrintIcon />, name: 'Print'},
        //{icon: <ShareIcon />, name: 'Share'},
    ] as Action[];

    return (
        <>
            <Dialog open={bookmarkModalOpen}>
                <DialogTitle>Create a New Bookmark</DialogTitle>
                <DialogContent>
                    <Grid container direction="column">
                        <Grid item>
                            <PLFormLabel>Bookmark Name</PLFormLabel>
                            <OutlinedInput
                                value={bookmarkModalBookmarkName}
                                onChange={(e) =>
                                    setBookmarkModalBookmarkName(e.target.value)
                                }
                            />
                        </Grid>
                        <CanI
                            functionId="Web_ShareBookmarksWithBillTos"
                            action={action.VIEW}>
                            <Grid item>
                                <PLFormLabel toolTip="Selecting a parent Bill To will show this book mark to all users of Child Bill Tos">
                                    Bill To's to share this bookmark with
                                </PLFormLabel>
                                <SSMultiSelect
                                    id="billtoSelect"
                                    value={bookmarkModalSelectedBilltos || []}
                                    // className={
                                    //     state?.billTos?.length > 0 ? 'filterActive' : ''
                                    // }
                                    options={getBilltosData}
                                    getOptionLabel={(option) => {
                                        return option.name;
                                    }}
                                    getOptionKey={(option) => option.btId}
                                    onChange={(event, value, reason, details) =>
                                        setBookmarkModalSelectedBilltos(value)
                                    }
                                />
                            </Grid>
                        </CanI>
                        <CanI
                            functionId="Web_MakeBookmarkPublic"
                            action={action.VIEW}>
                            <Grid item>
                                <PLFormLabel>
                                    Share this bookmark with EVERY user?
                                </PLFormLabel>
                                <Checkbox
                                    value={bookmarkModalPublicCheckBox}
                                    onChange={(event) =>
                                        setBookmarkModalPublicCheckBox(
                                            event.target.checked
                                        )
                                    }></Checkbox>
                            </Grid>
                        </CanI>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={cancelBookmarkCreate}>
                        Cancel Entry
                    </Button>
                    <Button color="primary" onClick={okBookmarkCreate}>
                        Create Bookmark
                    </Button>
                </DialogActions>
            </Dialog>
            <PLSpeedDial actions={actions}></PLSpeedDial>
            <Container sx={{display: "flex"}}>
                <Grid container direction="column">
                    {isEmbedError && (
                        <Grid item>
                            <Card sx={{marginBottom: 2, backgroundColor: '#fff3e0'}}>
                                <CardContent>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <Warning color="warning" />
                                        <Typography variant="h6">
                                            Failed to Load Report
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{marginTop: 1}}>
                                        {embedError?.data?.message || 'Unable to load PowerBI report. Please try again.'}
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="warning"
                                        onClick={() => refetchEmbed()}>
                                        Retry
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    )}
                    {isBillTosError && (
                        <Grid item>
                            <Card sx={{marginBottom: 2, backgroundColor: '#fff3e0'}}>
                                <CardContent>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <Warning color="warning" />
                                        <Typography variant="h6">
                                            Failed to Load BillTo List
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{marginTop: 1}}>
                                        {billTosError?.data?.message || 'Unable to load BillTo list for bookmark sharing. Please try again.'}
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="warning"
                                        onClick={() => refetchBillTos()}>
                                        Retry
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    )}
                    {errorMessage && (
                        <Grid item>
                            <Alert severity="error" sx={{marginBottom: 2}}>
                                {errorMessage}
                            </Alert>
                        </Grid>
                    )}
                    {/* {bookmarks?.some((x) => true) && (
                    <Grid item>
                        <ControlledLoading
                            loading={
                                createBookmarkIsLoading ||
                                deleteBookmarkIsLoading ||
                                bookmarksLoading
                            }>
                            <Grid container justifyContent="space-between">
                                <Grid item>
                                    <Grid container>
                                        <Grid item>
                                            <PLFormLabel>
                                                Saved Bookmarks
                                            </PLFormLabel>
                                            <Select
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                //@ts-ignore
                                                value={selectedBookmark}
                                                label="Age"
                                                onChange={handleChange}
                                                fullWidth={true}>
                                                {bookmarks.map((x) => (
                                                    <MenuItem value={x.id}>
                                                        {x.displayName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                onClick={handleRemoveBookmark}>
                                                Remove Bookmark
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Button onClick={openBookmarkModal}>
                                        Create Saved Bookmark
                                    </Button>{' '}
                                </Grid>
                            </Grid>
                        </ControlledLoading>
                    </Grid>
                )} */}
                    <Grid item flexGrow={2}>
                        {getEmbedInfoData && !getEmbedInfoLoading && (
                            <PowerBIEmbed
                                embedConfig={reportConfig}
                                cssClassName={'reportClass'}
                                eventHandlers={
                                    new Map([
                                        [
                                            'loaded',
                                            function (event, embed) {
                                                //This has to go here because it doesn't work if you put it out in the React code.
                                                //Some sort of race condition with the page change event
                                                //@ts-ignore
                                                embed.bookmarksManager
                                                    .getBookmarks()
                                                    .then((bookmarks) =>
                                                        dispatch(
                                                            setReportBookmarks(
                                                                bookmarks.map(
                                                                    (x) => {
                                                                        return {
                                                                            displayName:
                                                                                x.displayName,
                                                                            bookmarkBase64:
                                                                                x.state,
                                                                        };
                                                                    }
                                                                )
                                                            )
                                                        )
                                                    );
                                            },
                                        ],
                                        // ['rendered', function (event) {console.log('Report rendered'); console.log(event)}],
                                        // ['error', function (event) {console.log(event.detail);}],
                                        // ['visualClicked', () => console.log('visual clicked')],
                                        // ['pageChanged', (event) => console.log(event)],
                                    ])
                                }
                                getEmbeddedComponent={(embedObject) => {
                                    //@ts-ignore
                                    setReport(embedObject as Report);
                                }}
                            />
                        )}
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};

export default Reporting;
